/**
 * Client-side actions for highlight CTAs.
 *
 * Three behaviours:
 *   • subscribe → `mailto:` link (opens default mail client)
 *   • share     → `navigator.share()` with clipboard fallback
 *   • save      → downloads an .ics file (universal calendar import)
 *
 * All three are best-effort and degrade gracefully — they never throw into
 * the render tree.
 */

import type { HighlightEvent } from '../types';

export function runSubscribe(event: HighlightEvent | undefined): void {
  if (!event?.email) return;
  const params: string[] = [];
  if (event.emailSubject) params.push(`subject=${encodeURIComponent(event.emailSubject)}`);
  if (event.emailBody) params.push(`body=${encodeURIComponent(event.emailBody)}`);
  const qs = params.length ? `?${params.join('&')}` : '';
  window.location.href = `mailto:${event.email}${qs}`;
}

export async function runShare(event: HighlightEvent | undefined): Promise<void> {
  if (!event) return;
  const url = event.shareUrl ?? (typeof window !== 'undefined' ? window.location.href : '');
  const payload: ShareData = {
    title: event.shareTitle,
    text: event.shareText,
    url,
  };

  const nav = typeof navigator !== 'undefined' ? navigator : undefined;
  if (nav?.share && (!nav.canShare || nav.canShare(payload))) {
    try {
      await nav.share(payload);
      return;
    } catch (err) {
      // User cancelled or the share sheet errored — fall through to clipboard.
      if ((err as DOMException)?.name === 'AbortError') return;
    }
  }

  if (nav?.clipboard?.writeText && url) {
    try {
      await nav.clipboard.writeText(url);
      window.alert('Link copiado — partilha onde quiseres.');
      return;
    } catch {
      // ignore and fall through
    }
  }

  if (url) window.prompt('Copia o link para partilhar:', url);
}

export function runSave(event: HighlightEvent | undefined, fallbackSummary: string): void {
  if (!event) return;

  const summary = event.summary ?? fallbackSummary;
  const uid = `psl-${slugify(summary)}-${event.date ?? event.startsAt ?? Date.now()}@patinhassemlar`;
  const ics = buildIcs({
    uid,
    summary,
    location: event.location,
    description: event.description,
    url: event.shareUrl,
    date: event.date,
    startsAt: event.startsAt,
    endsAt: event.endsAt,
  });

  const filename = `${slugify(summary) || 'evento'}.ics`;
  downloadFile(filename, ics, 'text/calendar;charset=utf-8');
}

// ── ICS construction ────────────────────────────────────────────────────────

interface IcsInput {
  uid: string;
  summary: string;
  location?: string;
  description?: string;
  url?: string;
  date?: string;
  startsAt?: string;
  endsAt?: string;
}

function buildIcs(i: IcsInput): string {
  const lines: string[] = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Patinhas Sem Lar//Highlight//PT',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${i.uid}`,
    `DTSTAMP:${formatUtcStamp(new Date())}`,
  ];

  if (i.date) {
    // All-day VEVENT. DTEND for an all-day event is the day AFTER the last day.
    const start = i.date.replace(/-/g, '');
    const end = nextDayCompact(i.date);
    lines.push(`DTSTART;VALUE=DATE:${start}`);
    lines.push(`DTEND;VALUE=DATE:${end}`);
  } else if (i.startsAt) {
    lines.push(`DTSTART:${formatUtcStamp(new Date(i.startsAt))}`);
    if (i.endsAt) {
      lines.push(`DTEND:${formatUtcStamp(new Date(i.endsAt))}`);
    }
  }

  lines.push(`SUMMARY:${escapeIcs(i.summary)}`);
  if (i.location) lines.push(`LOCATION:${escapeIcs(i.location)}`);
  if (i.description) lines.push(`DESCRIPTION:${escapeIcs(i.description)}`);
  if (i.url) lines.push(`URL:${i.url}`);

  lines.push('END:VEVENT');
  lines.push('END:VCALENDAR');

  return lines.join('\r\n');
}

function formatUtcStamp(d: Date): string {
  // YYYYMMDDTHHMMSSZ
  const pad = (n: number) => String(n).padStart(2, '0');
  return (
    `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}` +
    `T${pad(d.getUTCHours())}${pad(d.getUTCMinutes())}${pad(d.getUTCSeconds())}Z`
  );
}

function nextDayCompact(isoDate: string): string {
  // isoDate "YYYY-MM-DD" → "YYYYMMDD" of the following day.
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 1);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getUTCFullYear()}${pad(d.getUTCMonth() + 1)}${pad(d.getUTCDate())}`;
}

function escapeIcs(text: string): string {
  return text
    .replace(/\\/g, '\\\\')
    .replace(/\n/g, '\\n')
    .replace(/,/g, '\\,')
    .replace(/;/g, '\\;');
}

function slugify(s: string): string {
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function downloadFile(filename: string, content: string, mime: string): void {
  if (typeof window === 'undefined' || typeof document === 'undefined') return;
  const blob = new Blob([content], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Revoke shortly after to let the download start.
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
